import * as Sentry from "@sentry/nextjs"
import { Accordion, Label, Spinner } from "flowbite-react"
import { FieldArray, Form, Formik, FormikHelpers, FormikProps } from "formik"
import React, { useEffect, useMemo } from "react"
import * as Yup from "yup"

import { DownIcon } from "@assets/icons"
import useToast from "@hooks/useToast"
import ListSuccessIcon from "@icons/ListSuccessIcon"
import ParticipantsIcon from "@icons/Participants"
import ParticipantsFields from "@modules/tenants/OrdersPage/Components/AddOrderModal/ParticipationDetailsForm/ParticipantsFields"
import { checkDisplayRules } from "@modules/tenants/OrdersPage/Components/AddOrderModal/ParticipationDetailsForm/ParticipationDetailsForm"
import { ProductInfoFormAttributes } from "@modules/tenants/OrdersPage/Components/AddOrderModal/ProductInfoForm/ProductInfoForm"
import EmptyItems from "@modules/tenants/OrdersPage/Components/EmptyItems"
import { PerOrderFieldsFormAttributes } from "@modules/tenants/OrdersPage/OrderSummaryPage/PerOrderFieldsModal"
import { ASK_PER_BOOKING } from "@mono/essentials/constants/options"
import { ASK_PER_PARTICIPANT } from "@mono/essentials/constants/options"
import { ProductType } from "@mono/essentials/types/OrderTypes"

import PerOrderDetailsForm from "../PerOrderDetailsForm"

export type ParticipationDetailsFormAttributes = {
  internalNotes: string
  participantsDetails?: Record<string, boolean | string | string[] | number>[]
}
export interface ParticipationDetailsFormProps {
  productInfo: ProductInfoFormAttributes
  onSuccess?: (arg0: ParticipationDetailsFormAttributes) => void
  error?: string
  handleClose: () => void
  goToBackStep?: () => void
  skipPage?: () => void
  selectedProduct?: ProductType
  formInitialValues?: ParticipationDetailsFormAttributes
  perOrderInitialValues?: any
  hideBackButton?: boolean
  selectedProductId?: string
  hideDefaultContainer?: boolean
  participantDetailsRef?: React.RefObject<FormikProps<ParticipationDetailsFormAttributes>>
  isIntegratedPage?: boolean
  isWidgetpage?: boolean
  focusedParticipantId?: string
  perOrderDetailsRef?: React.RefObject<FormikProps<PerOrderFieldsFormAttributes>>
  onPerOrderSucess?: (arg0: PerOrderFieldsFormAttributes) => void
  productFieldsData?: any
}

const ParticipationDetailsForm = ({
  onSuccess,
  productInfo,
  formInitialValues,
  participantDetailsRef,
  isIntegratedPage = false,
  isWidgetpage = false,
  error,
  focusedParticipantId,
  skipPage,
  perOrderDetailsRef,
  onPerOrderSucess,
  productFieldsData,
  perOrderInitialValues,
}: ParticipationDetailsFormProps) => {
  const { showToast } = useToast()

  const onSubmit = async (
    values: ParticipationDetailsFormAttributes,
    { setSubmitting, setFieldError }: FormikHelpers<ParticipationDetailsFormAttributes>
  ) => {
    try {
      setSubmitting(true)
      onSuccess?.(values)
      return values
    } catch (e: any) {
      Sentry.captureException(e)
      console.error(e.response.data.message)
      showToast({ heading: e.response.data.message, type: "danger" })
      const errors = e?.response?.data?.errors || []
      for (const key of Object.keys(errors)) {
        setFieldError(key, errors[key][0])
      }
    }
  }

  const initialValues = useMemo<ParticipationDetailsFormAttributes | undefined>(() => {
    if (productFieldsData) {
      const participantsFieldsArray: Record<string, boolean | string | string[] | number>[] = []
      ;(productInfo?.units || []).forEach((participantGroup) => {
        Array(parseInt(`${participantGroup.unitCount ?? 0}`, 10))
          .fill(1)
          .forEach((_, index) => {
            const fields: Record<string, boolean | string | string[] | number> = {}
            productFieldsData.forEach((productField) => {
              if (
                productField?.allowed_online_booking &&
                productField?.ask?.includes(ASK_PER_PARTICIPANT)
              ) {
                fields[productField.field.name] = productField.field.type === "checkbox" ? [] : ""
                fields[`${productField.field.name}_id`] = productField.id ?? ""
              }
            })
            fields.participantGroupName = participantGroup?.unitName
              ? `${participantGroup.unitName} ${participantGroup.unitCount > 1 ? index + 1 : ""}`
              : "Participant"
            fields.participantUnitId = participantGroup.unitId ?? ""
            participantsFieldsArray.push(fields)
          })
      })
      return {
        internalNotes: formInitialValues?.internalNotes ?? "",
        participantsDetails: formInitialValues?.participantsDetails
          ? formInitialValues.participantsDetails.map((pf, index) => ({
              ...pf,
              participantIndex: index,
            }))
          : participantsFieldsArray?.map((pf, index) => ({
              ...pf,
              participantIndex: index,
            })),
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productFieldsData])

  const perParticipantFields = productFieldsData?.filter(
    (item) => item?.ask?.includes("ask_per_participant") && item.allowed_online_booking
  )

  const perOrderFields = productFieldsData?.filter(
    (item) => item?.ask?.includes("ask_per_booking") && item.allowed_online_booking
  )

  useEffect(() => {
    if (
      productFieldsData &&
      initialValues &&
      perParticipantFields?.length === 0 &&
      perOrderFields?.length === 0
    ) {
      skipPage?.()
      onSuccess?.(participantDetailsRef?.current?.values)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perParticipantFields, perOrderFields])

  return productFieldsData?.filter(
    (field) =>
      (field.ask?.includes(ASK_PER_PARTICIPANT) || field.ask?.includes(ASK_PER_BOOKING)) &&
      checkDisplayRules({
        field,
        ticketInfo: productInfo?.units,
      }) === "show"
  )?.length && initialValues ? (
    <>
      <Formik
        initialValues={initialValues}
        innerRef={participantDetailsRef}
        validationSchema={() =>
          Yup.lazy((formValues: ParticipationDetailsFormAttributes) => {
            if (productFieldsData) {
              const perParticipantFields: Record<string, any> = {}
              productFieldsData.forEach((productField) => {
                if (
                  productField.allowed_online_booking &&
                  productField.ask?.includes(ASK_PER_PARTICIPANT)
                ) {
                  if (productField.field.type === "checkbox") {
                    perParticipantFields[productField.field.name] = Yup.array()
                      .of(Yup.string())
                      .when([], {
                        is: (participantIndex) => {
                          return (
                            (isWidgetpage
                              ? productField.required_for_online_booking
                              : productField.required_for_internal_booking) &&
                            checkDisplayRules({
                              field: productField,
                              formValues: formValues?.participantsDetails?.[participantIndex],
                              ticketInfo: productInfo?.units,
                            }) === "show"
                          )
                        },
                        then: (schema) =>
                          schema
                            .required(`*${productField.label} Required`)
                            .min(1, "*Atleast one is Required"),
                        otherwise: (schema) => schema.optional(),
                      })
                  } else if (productField.field.type === "inline") {
                    perParticipantFields[productField.field.name] = Yup.string().optional()
                  } else {
                    perParticipantFields[productField.field.name] = Yup.string().when(
                      ["participantIndex"],
                      {
                        is: (participantIndex) => {
                          return (
                            (isWidgetpage
                              ? productField.required_for_online_booking
                              : productField.required_for_internal_booking) &&
                            checkDisplayRules({
                              field: productField,
                              formValues: formValues?.participantsDetails?.[participantIndex],
                              ticketInfo: productInfo?.units,
                            }) === "show"
                          )
                        },
                        then: (schema) => {
                          if (productField.field.name === "email") {
                            return schema
                              .email("*Invalid email")
                              .required(`*${productField.label} Required`)
                          }
                          return schema.required(`*${productField.label} Required`)
                        },
                        otherwise: (schema) => {
                          if (productField.field.name === "email") {
                            return schema.email("*Invalid email").optional()
                          }
                          return schema.optional()
                        },
                      }
                    )
                  }
                }
              })

              return Yup.object().shape({
                internalNotes: Yup.string(),
                participantsDetails: Yup.array().of(Yup.object().shape(perParticipantFields)),
              })
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
          })
        }
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ getFieldProps, values, errors }) => {
          return (
            <Form id={"participantsDetailsForm"} autoComplete={"off"}>
              {perParticipantFields?.length > 0 &&
              perParticipantFields.filter(
                (field) =>
                  checkDisplayRules({
                    field,
                    ticketInfo: productInfo?.units,
                  }) === "show"
              )?.length ? (
                <div className={"flex flex-col"}>
                  <div>
                    <FieldArray {...getFieldProps("participantsDetails")}>
                      {() => {
                        return (
                          <>
                            {values?.participantsDetails && (
                              <>
                                <div
                                  className={"border-b mb-4 pb-3 md:mb-6 flex items-center gap-2"}
                                >
                                  <div>
                                    <ParticipantsIcon className={"text-neutral-600"} />
                                  </div>
                                  <Label className={"!text16Semibold"}>Participant Details</Label>
                                </div>
                                {values?.participantsDetails.map((fieldGroup, index) => (
                                  <Accordion
                                    alwaysOpen
                                    arrowIcon={() => (
                                      <DownIcon
                                        width={14}
                                        height={14}
                                        className={"text-neutral-500"}
                                      />
                                    )}
                                    key={index}
                                    collapseAll={
                                      typeof focusedParticipantId !== "undefined"
                                        ? fieldGroup.id !== focusedParticipantId
                                        : false
                                    }
                                    className={
                                      "mb-4 rounded-md border border-neutral-450 bg-whiteBlack-100"
                                    }
                                  >
                                    <Accordion.Panel>
                                      <Accordion.Title
                                        className={
                                          "relative !justify-between !bg-whiteBlack-100 !px-6 outline-none !ring-0 !rounded-md"
                                        }
                                      >
                                        <span className={"text14Semibold text-neutral-900"}>
                                          {fieldGroup.participantGroupName}
                                        </span>
                                      </Accordion.Title>
                                      <Accordion.Content
                                        className={"relative -mt-2 !border-0 !px-6 !pt-0"}
                                      >
                                        <div className={"md:grid sm:grid-cols-2 gap-4"}>
                                          <ParticipantsFields
                                            fieldsData={
                                              productFieldsData.filter(
                                                (field) =>
                                                  checkDisplayRules({
                                                    field,
                                                    formValues: fieldGroup,
                                                    ticketInfo: productInfo?.units,
                                                  }) === "show"
                                              ) ?? []
                                            }
                                            fieldGroupFormName={"participantsDetails"}
                                            fieldArrayIndex={index}
                                            isWidgetPage={isWidgetpage}
                                          />
                                        </div>
                                      </Accordion.Content>
                                    </Accordion.Panel>
                                  </Accordion>
                                ))}
                              </>
                            )}
                          </>
                        )
                      }}
                    </FieldArray>
                  </div>
                  {error ? <p className={"mx-6 mb-3 text14Regular text-red-600"}>{error}</p> : null}
                </div>
              ) : null}
            </Form>
          )
        }}
      </Formik>
      {perOrderFields?.length > 0 &&
      perOrderFields.filter(
        (field) =>
          checkDisplayRules({
            field,
            ticketInfo: productInfo?.units,
          }) === "show"
      )?.length ? (
        <div className={""}>
          <div className={"flex items-center border-b md:mb-6 mb-4 pb-3 md:gap-3 gap-2"}>
            <div>
              <ListSuccessIcon />
            </div>
            <Label className={"!text16Semibold"}>Add Booking Details</Label>
          </div>
          <div className={"bg-white rounded-md shadow-sm"}>
            <PerOrderDetailsForm
              onSuccess={onPerOrderSucess}
              isIntegratedPage={isIntegratedPage}
              productFieldsData={productFieldsData ?? []}
              perOrderDetailsRef={perOrderDetailsRef}
              productInfo={productInfo}
              perOrderInitialValues={perOrderInitialValues}
            />
          </div>
        </div>
      ) : null}
    </>
  ) : typeof productFieldsData !== "undefined" ? (
    <EmptyItems
      title={"There are no additional fields. Please click Next."}
      className={"flex-1 py-4"}
    />
  ) : (
    <div className={"py-5 text-center"}>
      <Spinner size={"md"} light />
    </div>
  )
}

export default ParticipationDetailsForm
